import { useCallback, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { useExpanded, useTable } from "react-table";
import { FormattedMessage, useIntl } from "react-intl";

import { formatWeight } from "helpers/commonHelpers";
import {
  calculateGoodItemsTotalWeight,
  getBaseWeightUnit,
} from "helpers/quotationFormHelper";
import { useGetShipmentInvoices } from "api/queries/invoice/useGetShipmentInvoices";
import DataTable from "components/organisms/DataTable/DataTable";
import FormHr from "pages/shipper/RequestForm/components/Common/FormHr";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Button from "components/atoms/Button/Button";
import getGoodsColumns from "./goodsColumns";
import GoodsRowSubComponent from "./components/GoodsRowSubComponent/GoodsRowSubComponent";
import "./InvoiceFormGoods.scss";
import AddItemDialog from "./components/AddItemDialog/AddItemDialog";
import useTableData from "./hooks/useTableData";

/**
 * Selects the current invoice from the data based on the current invoice id
 * @param data - The data to select the current invoice from
 * @param currentInvoiceId - The id of the current invoice that is selected
 * @returns - The current invoice object
 */
const currentInvoiceSelector = (data: any, currentInvoiceId: string) =>
  data.results.find((invoice: any) => invoice.invoice_id === currentInvoiceId);

function InvoiceFormGoods() {
  const { watch, control } = useFormContext();
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const currentInvoiceId = searchParams.get("invoice");
  const { id: shipmentId } = useParams();
  const actualGoods = watch("goods");
  const { append } = useFieldArray({ control, name: "goods" });
  const { data: currentInvoice, isLoading: isCurrentInvoiceLoading }: any = useGetShipmentInvoices({
    shipmentId: shipmentId!,
    options: {
      select: useCallback((data: any) => currentInvoiceSelector(data, currentInvoiceId!), [currentInvoiceId]),
      refetchOnMount: false,
    },
  });

  const isCustomsInvoice = currentInvoice?.invoice_type === "customs_broker_invoice";
  const tableData = useTableData(actualGoods, currentInvoice);

  const [isAddItemDialogOpen, setIsAddItemDialogOpen] = useState<boolean>(false);
  const [selectedCommodityIndex, setSelectedCommodityIndex] = useState<number>(-1);

  // Calculate the total weight of the actual goods in the invoice display UOM
  const actualWeightUnit = getBaseWeightUnit(currentInvoice?.good_items?.actual_total_weight_uom);
  const actualWeight =
    formatWeight(calculateGoodItemsTotalWeight(actualGoods, actualWeightUnit).weight) || "-";

  /**
   * Opens the AddItemDialog with the selected commodity index
   * @param commodityIndex - The actualGoods[index] of the commodity to edit
   */
  const onEditClick = (commodityIndex: number) => {
    setIsAddItemDialogOpen(true);
    setSelectedCommodityIndex(commodityIndex);
  };

  /**
   * Finds the previouslyrequested item with the given id and appends it to the actual goods
   * @param requestedItemId - The id of the requested item to restore
   */
  const onRestoreClick = (requestedItemId: number) => {
    const requestedItem = currentInvoice?.good_items?.requested_good_items.find((item: any) => item.id === requestedItemId);
    append(requestedItem);
  };

  /**
   * Closes the AddItemDialog and resets the selected commodity index
   */
  const onCloseAddItemDialog = () => {
    setIsAddItemDialogOpen(false);
    setSelectedCommodityIndex(-1);
  };

  // Expand row on click if the click is not on the edit/reset button or the modal
  const onRowClick = (row: any, event: any) => {
    const modalWrapper = document.getElementsByClassName("modal")[0];
    if (modalWrapper && modalWrapper.contains(event.target)) return;
    event.stopPropagation();
    if (event.target.id === "editIcon" || event.target.id === "restoreIcon") return;
    row.toggleRowExpanded();
  };

  // Define the columns and table instance
  const columns = useMemo(() => getGoodsColumns(intl, onEditClick, onRestoreClick, isCustomsInvoice), [isCustomsInvoice]);
  const instance = useTable(
    {
      columns,
      data: tableData,
    },
    useExpanded,
  );

  if (isCurrentInvoiceLoading)
    return (
      <div>
        <h5>
          <FormattedMessage id="general.goods" defaultMessage="Goods" />
        </h5>
        <LoadingSkeleton rows={1} />
      </div>
    );
  return (
    <>
      {isAddItemDialogOpen && (
        <AddItemDialog
          selectedCommodityIndex={selectedCommodityIndex}
          isOpen={isAddItemDialogOpen}
          onClose={onCloseAddItemDialog}
          currentInvoice={currentInvoice}
        />
      )}
      <div>
        <h5>
          <FormattedMessage id="general.goods" defaultMessage="Goods" />
        </h5>
        {/* Goods Table */}
        <div className="invoice-form-goods-table">
          <DataTable
            tableInstance={instance}
            totalData={tableData.length}
            draggable={false}
            RowSubComponent={({ row }: any) => <GoodsRowSubComponent commodityData={row.original} />}
            customNoResults={`0 ${intl.formatMessage({ id: "general.goods", defaultMessage: "Goods" })}`}
            onRowClick={onRowClick}
          />
        </div>
        {!isCustomsInvoice && (
          <Button
            variant="secondary"
            className="ml-auto mt-3"
            onClick={() => setIsAddItemDialogOpen(true)}
            label={intl.formatMessage({
              id: "billing.invoiceForm.addAdditionalItem",
              defaultMessage: "Add Additional Item",
            })}
          />
        )}
        <FormHr />
        {/* Footer */}
        <div className="d-flex justify-content-between">
          <p className="font-weight-bold">
            <FormattedMessage id="quote-request-detail.total" defaultMessage="Total" />
          </p>
          <div>
            <p className="font-b2 mb-1">
              <FormattedMessage id="billing.invoiceForm.quotedWeight" defaultMessage="Quoted Weight" />
            </p>
            <p className="font-b9">
              {currentInvoice.good_items.requested_total_weight || "-"}{" "}
              {currentInvoice.good_items.requested_total_weight_uom}
            </p>
          </div>
          <div>
            <p className="font-b2 mb-1">
              <FormattedMessage id="billing.invoiceForm.actualWeight" defaultMessage="Actual Weight" />
            </p>
            <p className="font-b9">
              {actualWeight} {currentInvoice.good_items.actual_total_weight_uom}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default InvoiceFormGoods;
